import {
  ChevronDown,
  Copy,
  Download,
  Eye,
  EyeOff,
  ImageUp,
  KeyRound,
  Languages,
  Loader2,
  Monitor,
  Moon,
  Redo2,
  ScanSearch,
  SquareDashedMousePointer,
  Sun,
  Trash2,
  Undo2
} from "lucide-react";
import { type DragEvent, type KeyboardEvent, type PointerEvent, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type {
  DetectionBox,
  DetectionCategory,
  FaceDetectionDiagnostics,
  LocalAiProfile,
  LocalAiVlmDiagnostics,
  LocalAiVlmTile,
  MaskSettings,
  MemoryProfile,
  Rect
} from "@aimosaic/core";
import { DEFAULT_MASK_SETTINGS, dedupeBoxes, TEXT_CATEGORIES } from "@aimosaic/core";
import { detectPatternBoxesWithDiagnostics } from "@aimosaic/detectors";
import { createLocalAiProvider } from "@aimosaic/providers";
import {
  LOCALE_LABELS,
  categoryLabel,
  productName,
  resolveTheme,
  sourceLabel,
  t
} from "@aimosaic/ui";
import type { Appearance, Locale, TranslationKey } from "@aimosaic/ui";
import { type BatchItem, createBatchZipDownloadName, exportBatchZip, runBatchDetection, toBatchItems } from "./batch";
import { mergeAiVisionBoxes, shouldShowAiNoValidDetections } from "./aiDetection";
import { APP_VERSION, CREATOR_LABEL, GUIDE_URL } from "./branding";
import { detectFaces } from "./detection/face";
import {
  type OcrDiagnostics,
  type OcrRecognition,
  mergeOcrLines,
  recognizeOcrLines,
  recognizeUrlOcrLines
} from "./detection/ocr";
import { shouldRunDetailedUrlOcr } from "./detection/detectionMode";
import { closeDetailsMenus, isDirectBackdropPointer, isOutsideElement } from "./domInteractions";
import { pickImageFilesFromClipboardItems, pickImageFilesFromList } from "./fileInput";
import { exportMaskedImage } from "./imageMasking";
import { isLocalAiFeatureEnabled, isLocalAiRuntimeEnabled } from "./localAiSettings";
import { classifyCandidateStrength } from "./localAiReview";
import { recoverOcrTextHintBoxes } from "./ocrTextHints";
import { type RenderedImage, createRenderedImage, revokeRenderedImage } from "./renderedImage";
import { type ResizeHandle, moveBoxByDelta, resizeBoxFromHandle } from "./state/boxEditing";
import { boxesReducer, initialBoxesState } from "./state/boxes";
import {
  DEFAULT_CATEGORIES,
  DEFAULT_LOCAL_AI_SETTINGS,
  DEFAULT_PANEL_STATE,
  type LocalAiSettings,
  type PanelId,
  type PanelState,
  loadPreferences,
  rememberIgnoredText,
  saveAppearance,
  saveCategories,
  saveDeepSearch,
  saveLocalAiSettings,
  saveLocale,
  saveMaskSettings,
  savePanelState
} from "./storage/preferences";
import { planVlmTiles } from "./vlmTiles";

interface LoadedImage {
  url: string;
  dataUrl: string;
  name: string;
  width: number;
  height: number;
}

interface DragState {
  start: { x: number; y: number };
  current: { x: number; y: number };
}

interface BoxEditDragState {
  boxId: string;
  mode: "move" | ResizeHandle;
  start: { x: number; y: number };
  originalBox: Rect;
  currentBox: Rect;
}

interface SessionInfo {
  authMode: "none" | "ai-only" | "all";
  authenticated: boolean;
}

interface StatusMessage {
  key: TranslationKey;
  values?: Record<string, string | number>;
}

interface DetectionDiagnostics {
  ocr: OcrDiagnostics;
  urlOcr: OcrDiagnostics;
  patternCount: number;
  contextCandidateCount: number;
  recoveredCandidateCount: number;
  layoutCandidateCount: number;
  faceCount: number;
  faceDiagnostics: FaceDetectionDiagnostics;
  localAiCount: number;
  localAiReviewedCount: number;
  localAiDroppedCount: number;
  localAiChangedCount: number;
  localAiNeedsReviewCount: number;
  vlmCount: number;
  vlmTileCount: number;
  vlmFallback: boolean;
  vlmRejectedCount: number;
  vlmInvalidCategoryCount: number;
  vlmPlaceholderRejectedCount: number;
  vlmParseErrorCount: number;
  vlmModel?: string;
  vlmHttpStatus?: number;
  vlmSchemaMode?: boolean;
  vlmRawResponseExcerpt?: string;
  aiElapsedMs: number;
  finalCount: number;
}

function publicAssetPath(path: string) {
  const base = import.meta.env.BASE_URL;
  return `${base}${base.endsWith("/") ? "" : "/"}${path.replace(/^\/+/, "")}`;
}

interface VlmMainDetectionResult {
  boxes: DetectionBox[];
  diagnostics?: LocalAiVlmDiagnostics;
  tileCount: number;
  elapsedMs: number;
}

type LocalAiConnectionStatus = "idle" | "ready" | "unavailable";

const LOCAL_AI_FEATURE_ENABLED = isLocalAiFeatureEnabled();

const ALL_CATEGORIES: DetectionCategory[] = [
  "ip",
  "url",
  "email",
  "token",
  "secret",
  "resident-id",
  "phone",
  "face"
];

export function App() {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const overlayRef = useRef<SVGSVGElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const boxListRef = useRef<HTMLDivElement | null>(null);
  const toolbarMenusRef = useRef<HTMLDivElement | null>(null);
  const languageMenuRef = useRef<HTMLDetailsElement | null>(null);
  const themeMenuRef = useRef<HTMLDetailsElement | null>(null);
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [boxesState, dispatchBoxes] = useReducer(boxesReducer, initialBoxesState);
  const [categories, setCategories] = useState<DetectionCategory[]>(DEFAULT_CATEGORIES);
  const [maskSettings, setMaskSettings] = useState<MaskSettings>(DEFAULT_MASK_SETTINGS);
  const [memory, setMemory] = useState<MemoryProfile>({ ignoredTexts: [] });
  const [locale, setLocale] = useState<Locale>("ko");
  const [appearance, setAppearance] = useState<Appearance>("system");
  const [deepSearch, setDeepSearch] = useState(false);
  const [localAiSettings, setLocalAiSettings] = useState<LocalAiSettings>(DEFAULT_LOCAL_AI_SETTINGS);
  const [localAiStatus, setLocalAiStatus] = useState<LocalAiConnectionStatus>("idle");
  const [panelState, setPanelState] = useState<PanelState>(DEFAULT_PANEL_STATE);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);
  const [status, setStatus] = useState<StatusMessage>({ key: "status.ready" });
  const [toast, setToast] = useState<StatusMessage | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [session, setSession] = useState<SessionInfo>({ authMode: "none", authenticated: false });
  const [password, setPassword] = useState("");
  const [loginMessage, setLoginMessage] = useState("");
  const [renderedImage, setRenderedImage] = useState<RenderedImage | null>(null);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [detectionDiagnostics, setDetectionDiagnostics] = useState<DetectionDiagnostics | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [boxEditDrag, setBoxEditDrag] = useState<BoxEditDragState | null>(null);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const [activeBatchItemId, setActiveBatchItemId] = useState<string | null>(null);
  const [isBatchRunning, setIsBatchRunning] = useState(false);

  const activeBoxes = boxesState.present;
  const displayBoxes = boxEditDrag
    ? activeBoxes.map((box) => (box.id === boxEditDrag.boxId ? { ...box, bbox: boxEditDrag.currentBox } : box))
    : activeBoxes;
  const selectedBox = displayBoxes.find((box) => box.id === selectedBoxId) ?? null;
  const maskedCount = activeBoxes.filter((box) => box.action === "mask").length;
  const ignoredCount = activeBoxes.length - maskedCount;
  const theme = resolveTheme(appearance, systemPrefersDark);
  const statusText = t(locale, status.key, status.values);
  const productTitle = productName(locale);
  const ThemeIcon = appearance === "dark" ? Moon : appearance === "light" ? Sun : Monitor;
  const completedBatchCount = batchItems.filter((item) => item.status === "done").length;
  const batchZipReady = batchItems.some((item) => item.status === "done" && item.renderedImage);
  const localAiRuntimeEnabled = isLocalAiRuntimeEnabled(localAiSettings);

  const preset = useMemo(
    () => ({ id: "custom", label: "사용자 프리셋", categories }),
    [categories]
  );

  useEffect(() => {
    void loadPreferences().then((preferences) => {
      setCategories(preferences.categories);
      setMaskSettings(preferences.maskSettings);
      setMemory(preferences.memory);
      setLocale(preferences.locale);
      setAppearance(preferences.appearance);
      setDeepSearch(preferences.deepSearch);
      setLocalAiSettings(preferences.localAi);
      setPanelState(preferences.panelState);
      setPreferencesReady(true);
    });
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setSystemPrefersDark(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dataset.theme = theme;
    document.title = productTitle;
  }, [locale, productTitle, theme]);

  useEffect(() => {
    void refreshSession();
  }, []);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveCategories(categories);
  }, [categories, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveMaskSettings(maskSettings);
  }, [maskSettings, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveLocale(locale);
  }, [locale, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveAppearance(appearance);
  }, [appearance, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveDeepSearch(deepSearch);
  }, [deepSearch, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void saveLocalAiSettings(localAiSettings);
  }, [localAiSettings, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady) return;
    void savePanelState(panelState);
  }, [panelState, preferencesReady]);

  useEffect(() => {
    if (!preferencesReady || !localAiRuntimeEnabled) {
      setLocalAiStatus("idle");
      return;
    }

    let isCurrent = true;
    const provider = createLocalAiProvider({ baseUrl: localAiSettings.endpoint });
    void provider
      .health()
      .then((health) => {
        if (!isCurrent) return;
        setLocalAiStatus(health.ok && health.vlmReady !== false ? "ready" : "unavailable");
      })
      .catch(() => {
        if (isCurrent) setLocalAiStatus("unavailable");
      });

    return () => {
      isCurrent = false;
    };
  }, [localAiRuntimeEnabled, localAiSettings.endpoint, preferencesReady]);

  useEffect(() => {
    return () => {
      if (image?.url) URL.revokeObjectURL(image.url);
      revokeRenderedImage(renderedImage);
    };
  }, [image?.url, renderedImage]);

  useEffect(() => {
    if (selectedBoxId && !activeBoxes.some((box) => box.id === selectedBoxId)) {
      setSelectedBoxId(null);
    }
  }, [activeBoxes, selectedBoxId]);

  useEffect(() => {
    if (!activeBatchItemId) return;
    setBatchItems((current) =>
      current.map((item) =>
        item.id === activeBatchItemId
          ? {
              ...item,
              boxes: activeBoxes,
              diagnostics: detectionDiagnostics ?? item.diagnostics,
              renderedImage: renderedImage ?? item.renderedImage
            }
          : item
      )
    );
  }, [activeBatchItemId, activeBoxes, detectionDiagnostics, renderedImage]);

  useEffect(() => {
    if (!selectedBoxId) return;
    const selector = `[data-box-id="${CSS.escape(selectedBoxId)}"]`;
    boxListRef.current?.querySelector(selector)?.scrollIntoView({ block: "nearest" });
  }, [selectedBoxId]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    function handlePointerDown(event: globalThis.PointerEvent) {
      if (isOutsideElement(event.target, toolbarMenusRef.current)) {
        closeToolbarMenus();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (event.key === "Escape") {
        if (isResultOpen) {
          event.preventDefault();
          setIsResultOpen(false);
          return;
        }
        closeToolbarMenus();
        if (target?.closest("input, select, textarea")) return;
        event.preventDefault();
        setSelectedBoxId(null);
        return;
      }

      if (target?.closest("input, select, textarea, button")) return;
      if ((event.key === "Delete" || event.key === "Backspace") && selectedBoxId) {
        event.preventDefault();
        removeBox(selectedBoxId);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isResultOpen, selectedBoxId]);

  useEffect(() => {
    function handlePaste(event: ClipboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, select, textarea") || target?.isContentEditable) return;

      const imageFiles = pickImageFilesFromClipboardItems(event.clipboardData?.items);
      if (!imageFiles.length) return;
      event.preventDefault();
      void handleFiles(imageFiles);
    }

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  });

  function closeToolbarMenus() {
    closeDetailsMenus(languageMenuRef.current, themeMenuRef.current);
  }

  function showToast(message: StatusMessage) {
    setToast(message);
  }

  async function handleFiles(files: Iterable<File> | FileList | null | undefined) {
    const imageFiles = pickImageFilesFromList(files);
    if (!imageFiles.length) {
      setStatus({ key: "status.imageOnly" });
      return;
    }

    setIsBusy(true);
    setStatus({ key: "status.loadingImage" });
    dispatchBoxes({ type: "reset" });
    setSelectedBoxId(null);
    setBoxEditDrag(null);
    revokeRenderedImage(renderedImage);
    setRenderedImage(null);
    setIsResultOpen(false);
    setDetectionDiagnostics(null);

    try {
      const items = await toBatchItems(imageFiles, { readDimensions: readImageDimensions });
      setBatchItems(items);
      const first = items[0];
      if (!first?.image) {
        setImage(null);
        setActiveBatchItemId(null);
        setStatus({ key: "status.imageOnly" });
        return;
      }
      loadBatchItem(first);
      setStatus(
        items.length > 1
          ? { key: "status.batchReady", values: { count: items.length } }
          : { key: "status.imageReady" }
      );
    } catch {
      setStatus({ key: "errors.detectionFailed" });
    } finally {
      setIsBusy(false);
    }
  }

  function loadBatchItem(item: BatchItem) {
    if (!item.image) return;
    setActiveBatchItemId(item.id);
    setImage({
      url: item.image.dataUrl,
      dataUrl: item.image.dataUrl,
      name: item.image.name,
      width: item.image.width,
      height: item.image.height
    });
    dispatchBoxes({ type: "set", boxes: item.boxes });
    setSelectedBoxId(item.boxes[0]?.id ?? null);
    setBoxEditDrag(null);
    setRenderedImage(null);
    setDetectionDiagnostics((item.diagnostics as DetectionDiagnostics | undefined) ?? null);
    setIsResultOpen(false);
  }

  function setPanelOpen(panelId: PanelId, open: boolean) {
    setPanelState((current) => (current[panelId] === open ? current : { ...current, [panelId]: open }));
  }

  async function runDetection() {
    if (!image || !imageRef.current) return;
    setIsBusy(true);

    try {
      const result = await detectImage(image, imageRef.current, deepSearch);
      applyDetectionResult(result);
    } catch (error) {
      setDetectionDiagnostics(null);
      setStatus({ key: "errors.detectionFailed" });
    } finally {
      setIsBusy(false);
    }
  }

  async function detectImage(
    targetImage: LoadedImage,
    targetElement: HTMLImageElement,
    useDetailedSearch: boolean
  ): Promise<{ boxes: DetectionBox[]; diagnostics: DetectionDiagnostics }> {
    const safetyNetResult = await detectWithLocalSafetyNet(targetImage, targetElement, useDetailedSearch, false);
    if (!localAiRuntimeEnabled) return safetyNetResult;

    const vlmResult = await detectWithVlmMain(targetImage, targetElement);
    if (!vlmResult) {
      return {
        boxes: safetyNetResult.boxes,
        diagnostics: {
          ...safetyNetResult.diagnostics,
          vlmFallback: true,
          finalCount: safetyNetResult.boxes.length
        }
      };
    }

    const boxes = mergeAiVisionBoxes(vlmResult.boxes, [], safetyNetResult.boxes);
    const keptVlmCount = boxes.filter((box) => box.source === "local-vlm").length;
    const clientRejectedVlmCount = Math.max(0, vlmResult.boxes.length - keptVlmCount);
    return {
      boxes,
      diagnostics: {
        ...safetyNetResult.diagnostics,
        localAiCount: keptVlmCount,
        localAiNeedsReviewCount: boxes.filter((box) => box.source === "local-vlm" && box.requiresReview).length,
        vlmCount: vlmResult.boxes.length,
        vlmTileCount: vlmResult.tileCount,
        vlmFallback: false,
        vlmRejectedCount: (vlmResult.diagnostics?.rejectedCount ?? 0) + clientRejectedVlmCount,
        vlmInvalidCategoryCount: vlmResult.diagnostics?.invalidCategoryCount ?? 0,
        vlmPlaceholderRejectedCount: vlmResult.diagnostics?.placeholderRejectedCount ?? 0,
        vlmParseErrorCount: vlmResult.diagnostics?.parseErrorCount ?? 0,
        vlmModel: vlmResult.diagnostics?.model,
        vlmHttpStatus: vlmResult.diagnostics?.httpStatus,
        vlmSchemaMode: vlmResult.diagnostics?.schemaMode,
        vlmRawResponseExcerpt: vlmResult.diagnostics?.rawResponseExcerpt,
        aiElapsedMs: vlmResult.elapsedMs,
        finalCount: boxes.length
      }
    };
  }

  async function detectWithLocalSafetyNet(
    targetImage: LoadedImage,
    targetElement: HTMLImageElement,
    useDetailedSearch: boolean,
    vlmFallback: boolean,
    vlmHintBoxes: DetectionBox[] = []
  ): Promise<{ boxes: DetectionBox[]; diagnostics: DetectionDiagnostics }> {
    setStatus({ key: "status.localOcr" });
    const ocr = await recognizeOcrLines(targetImage.dataUrl, targetImage.width, targetImage.height);
    let urlOcr = emptyOcrRecognition(true);
    let patternResult = detectPatternBoxesWithDiagnostics(ocr.lines, preset, memory);
    let patternBoxes = patternResult.boxes;
    if (shouldRunDetailedUrlOcr(useDetailedSearch, categories)) {
      urlOcr = await recognizeUrlOcrLines(targetImage.dataUrl, targetImage.width, targetImage.height, ocr.lines);
      patternResult = detectPatternBoxesWithDiagnostics(mergeOcrLines(ocr.lines, urlOcr.lines), preset, memory);
      patternBoxes = patternResult.boxes;
    }
    const ocrHintBoxes = recoverOcrTextHintBoxes(mergeOcrLines(ocr.lines, urlOcr.lines), vlmHintBoxes, preset.categories, memory);
    patternBoxes = dedupeBoxes([...patternBoxes, ...ocrHintBoxes], 0.2);
    setStatus({ key: "status.face" });
    const faceResult = categories.includes("face")
      ? await detectFaces(targetElement)
      : { boxes: [], diagnostics: emptyFaceDiagnostics() };
    const faceBoxes = faceResult.boxes;
    const merged = mergeAiVisionBoxes([], [], [...patternBoxes, ...faceBoxes]);

    return {
      boxes: merged,
      diagnostics: {
        ocr: ocr.diagnostics,
        urlOcr: urlOcr.diagnostics,
        patternCount: patternBoxes.length,
        contextCandidateCount: patternResult.diagnostics.contextCandidateCount,
        recoveredCandidateCount: patternResult.diagnostics.recoveredCandidateCount,
        layoutCandidateCount: patternResult.diagnostics.layoutCandidateCount,
        faceCount: faceBoxes.length,
        faceDiagnostics: faceResult.diagnostics,
        localAiCount: 0,
        localAiReviewedCount: 0,
        localAiDroppedCount: 0,
        localAiChangedCount: 0,
        localAiNeedsReviewCount: 0,
        vlmCount: 0,
        vlmTileCount: 0,
        vlmFallback,
        vlmRejectedCount: 0,
        vlmInvalidCategoryCount: 0,
        vlmPlaceholderRejectedCount: 0,
        vlmParseErrorCount: 0,
        vlmModel: undefined,
        vlmHttpStatus: undefined,
        vlmSchemaMode: undefined,
        vlmRawResponseExcerpt: undefined,
        aiElapsedMs: 0,
        finalCount: merged.length
      }
    };
  }

  function applyDetectionResult(result: { boxes: DetectionBox[]; diagnostics: DetectionDiagnostics }) {
    dispatchBoxes({ type: "set", boxes: result.boxes });
    setSelectedBoxId(result.boxes[0]?.id ?? null);
    setDetectionDiagnostics(result.diagnostics);
    setStatus(
      shouldShowAiNoValidDetections(result.diagnostics)
        ? { key: "status.aiNoValidDetections" }
        : { key: "status.detected", values: { count: result.boxes.length } }
    );
  }

  async function detectWithVlmMain(
    targetImage: LoadedImage,
    targetElement: HTMLImageElement
  ): Promise<VlmMainDetectionResult | null> {
    setStatus({ key: "status.localAi" });
    const startedAt = performance.now();
    const provider = createLocalAiProvider({ baseUrl: localAiSettings.endpoint });

    let health;
    try {
      health = await provider.health();
    } catch {
      setLocalAiStatus("unavailable");
      return null;
    }

    if (!health.ok || health.vlmReady === false) {
      setLocalAiStatus("unavailable");
      return null;
    }

    setLocalAiStatus("ready");
    const tileMaxEdge = localAiSettings.profile === "precise" ? 1600 : 1280;
    const tiles = buildVlmTiles(targetImage, targetElement, localAiSettings.profile, tileMaxEdge);
    let vlmBoxes: DetectionBox[] = [];
    let vlmDiagnostics: LocalAiVlmDiagnostics | undefined;

    try {
      const vlmResult = await provider.detectVlmMain({
        image: {
          width: targetImage.width,
          height: targetImage.height,
          dataUrl: createLocalAiPreviewDataUrl(targetElement, tileMaxEdge)
        },
        tiles,
        preset,
        profile: localAiSettings.profile
      });
      vlmBoxes = vlmResult.boxes;
      vlmDiagnostics = vlmResult.diagnostics;
    } catch (error) {
      const message = error instanceof Error ? error.message : "local_ai_vlm_request_failed";
      vlmDiagnostics = {
        acceptedCount: 0,
        rejectedCount: 0,
        invalidCategoryCount: 0,
        placeholderRejectedCount: 0,
        parseErrorCount: 1,
        httpStatus: parseHttpStatusFromError(message),
        rawResponseExcerpt: message
      };
    }

    const elapsedMs = Math.round(performance.now() - startedAt);
    return {
      boxes: vlmBoxes,
      diagnostics: vlmDiagnostics,
      tileCount: tiles.length,
      elapsedMs
    };
  }

  async function refreshSession() {
    try {
      const response = await fetch("/api/session", { credentials: "include" });
      if (!response.ok) return;
      const payload = (await response.json()) as SessionInfo;
      setSession(payload);
    } catch {
      setSession({ authMode: "none", authenticated: false });
    }
  }

  async function login() {
    setLoginMessage(t(locale, "auth.checking"));
    const response = await fetch("/api/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password })
    });
    if (!response.ok) {
      setLoginMessage(t(locale, "auth.invalid"));
      return;
    }
    setPassword("");
    setLoginMessage(t(locale, "auth.success"));
    await refreshSession();
  }

  async function renderMaskedImage(
    outputType: "png" | "jpeg" = "png",
    options: { notify?: boolean } = {}
  ): Promise<RenderedImage | null> {
    if (!image || !imageRef.current) return null;
    setIsBusy(true);
    setStatus({ key: "status.renderStart" });
    try {
      const result = await exportMaskedImage(
        imageRef.current,
        activeBoxes,
        maskSettings,
        image.name,
        outputType,
        image.dataUrl
      );
      revokeRenderedImage(renderedImage);
      const next = createRenderedImage(result.blob, result.filename, outputType);
      setRenderedImage(next);
      setIsResultOpen(true);
      setStatus({ key: "status.resultReady" });
      if (options.notify) showToast({ key: "status.resultReady" });
      return next;
    } catch (error) {
      setStatus({ key: "status.downloadFailed" });
      return null;
    } finally {
      setIsBusy(false);
    }
  }

  async function downloadRenderedImage(outputType: "png" | "jpeg") {
    const target =
      renderedImage?.outputType === outputType ? renderedImage : await renderMaskedImage(outputType);
    if (!target) return;

    const anchor = document.createElement("a");
    anchor.href = target.url;
    anchor.download = target.filename;
    anchor.click();
    setStatus({ key: "status.downloadReady", values: { filename: target.filename } });
  }

  async function runBatchQueue() {
    if (!batchItems.length || isBatchRunning) return;
    setIsBatchRunning(true);
    setIsBusy(true);
    setStatus({ key: "status.batchRunning", values: { current: 0, total: batchItems.length } });

    try {
      const result = await runBatchDetection(
        batchItems,
        async (item) => {
          if (!item.image) throw new Error("Image is missing");
          const targetImage: LoadedImage = {
            url: item.image.dataUrl,
            dataUrl: item.image.dataUrl,
            name: item.image.name,
            width: item.image.width,
            height: item.image.height
          };
          const targetElement = await loadHtmlImage(item.image.dataUrl);
          const detection = await detectImage(targetImage, targetElement, false);
          const rendered = detection.boxes.some((box) => box.action === "mask")
            ? await renderBatchItemImage(targetElement, targetImage, detection.boxes)
            : undefined;
          return { ...detection, renderedImage: rendered };
        },
        (item, index) => {
          setBatchItems((current) => current.map((candidate) => (candidate.id === item.id ? item : candidate)));
          if (item.status === "running") {
            loadBatchItem(item);
            setStatus({ key: "status.batchRunning", values: { current: index + 1, total: batchItems.length } });
          }
        },
        { rerunDone: true }
      );

      setBatchItems(result);
      const nextActive = result.find((item) => item.id === activeBatchItemId) ?? result[0];
      if (nextActive) loadBatchItem(nextActive);
      setStatus({ key: "status.batchDone", values: { count: result.filter((item) => item.status === "done").length } });
    } catch {
      setStatus({ key: "errors.detectionFailed" });
    } finally {
      setIsBusy(false);
      setIsBatchRunning(false);
    }
  }

  async function renderBatchItemImage(
    targetElement: HTMLImageElement,
    targetImage: LoadedImage,
    boxes: DetectionBox[]
  ): Promise<RenderedImage> {
    const result = await exportMaskedImage(
      targetElement,
      boxes,
      maskSettings,
      targetImage.name,
      "png",
      targetImage.dataUrl
    );
    return createRenderedImage(result.blob, result.filename, "png");
  }

  async function downloadBatchZip() {
    const readyItems = batchItems.filter((item) => item.status === "done" && item.renderedImage);
    if (!readyItems.length) return;
    const blob = await exportBatchZip(readyItems);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = createBatchZipDownloadName();
    anchor.click();
    URL.revokeObjectURL(url);
    setStatus({ key: "status.batchZipReady" });
  }

  async function copyRenderedImage() {
    const target = renderedImage?.outputType === "png" ? renderedImage : await renderMaskedImage("png");
    if (!target) return;
    const ClipboardItemCtor = globalThis.ClipboardItem;
    if (!navigator.clipboard?.write || !ClipboardItemCtor) {
      setStatus({ key: "status.copyUnsupported" });
      showToast({ key: "status.copyUnsupported" });
      return;
    }

    try {
      await navigator.clipboard.write([new ClipboardItemCtor({ "image/png": target.blob })]);
      setStatus({ key: "status.copyReady" });
      showToast({ key: "status.copyReady" });
    } catch {
      setStatus({ key: "status.copyUnsupported" });
      showToast({ key: "status.copyUnsupported" });
    }
  }

  async function toggleBox(box: DetectionBox) {
    dispatchBoxes({ type: "toggle-action", id: box.id });
    if (box.action === "mask" && box.text) {
      setMemory(await rememberIgnoredText(box.text));
    }
  }

  function removeBox(id: string) {
    dispatchBoxes({ type: "remove", id });
    if (selectedBoxId === id) {
      setSelectedBoxId(null);
    }
  }

  function pointerToImagePoint(event: PointerEvent) {
    if (!image || !overlayRef.current) return { x: 0, y: 0 };
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * image.width,
      y: ((event.clientY - rect.top) / rect.height) * image.height
    };
  }

  function startBoxEdit(event: PointerEvent, box: DetectionBox, mode: BoxEditDragState["mode"]) {
    event.stopPropagation();
    setSelectedBoxId(box.id);
    const point = pointerToImagePoint(event);
    setBoxEditDrag({
      boxId: box.id,
      mode,
      start: point,
      originalBox: box.bbox,
      currentBox: box.bbox
    });
  }

  function updateBoxEdit(event: PointerEvent) {
    if (!boxEditDrag || !image) return;
    const point = pointerToImagePoint(event);
    const nextBox =
      boxEditDrag.mode === "move"
        ? moveBoxByDelta(
            boxEditDrag.originalBox,
            { x: point.x - boxEditDrag.start.x, y: point.y - boxEditDrag.start.y },
            image.width,
            image.height
          )
        : resizeBoxFromHandle(boxEditDrag.originalBox, boxEditDrag.mode, point, image.width, image.height);
    setBoxEditDrag({ ...boxEditDrag, currentBox: nextBox });
  }

  function finishBoxEdit() {
    if (!boxEditDrag) return;
    if (!rectsEqual(boxEditDrag.originalBox, boxEditDrag.currentBox)) {
      dispatchBoxes({
        type: "update",
        id: boxEditDrag.boxId,
        box: { bbox: boxEditDrag.currentBox }
      });
    }
    setBoxEditDrag(null);
  }

  function handleBoxItemKeyDown(event: KeyboardEvent<HTMLElement>, boxId: string) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    setSelectedBoxId(boxId);
  }

  function finishManualBox() {
    if (!drag || !image) return;
    const rect = normalizeRect(drag.start, drag.current);
    setDrag(null);
    if (rect.width < 6 || rect.height < 6) return;
    const id = `manual-${Date.now()}`;
    dispatchBoxes({
      type: "add",
      box: {
        id,
        category: "manual",
        bbox: rect,
        confidence: 1,
        source: "manual",
        action: "mask"
      }
    });
    setSelectedBoxId(id);
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    setIsDraggingFile(true);
  }

  function handleDragLeave(event: DragEvent<HTMLElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsDraggingFile(false);
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setIsDraggingFile(false);
    void handleFiles(event.dataTransfer.files);
  }

  if (session.authMode === "all" && !session.authenticated) {
    return (
      <main className="loginShell">
        <section className="loginPanel">
          <div>
            <p className="eyebrow">{t(locale, "auth.required")}</p>
            <h1>{productTitle}</h1>
            <p className="muted">{t(locale, "auth.allMode")}</p>
          </div>
          <label className="field">
            <span>{t(locale, "auth.password")}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void login();
              }}
            />
          </label>
          <button type="button" className="primaryButton" onClick={() => void login()}>
            <KeyRound size={18} />
            {t(locale, "auth.login")}
          </button>
          {loginMessage ? <p className="muted">{loginMessage}</p> : null}
        </section>
      </main>
    );
  }

  return (
    <main
      className={isDraggingFile ? "appShell draggingFile" : "appShell"}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <section className="toolbar">
        <div className="brandLockup">
          <img className="brandLogo" src={publicAssetPath("icons/icon-192.png")} alt="" aria-hidden="true" />
          <div className="brandText">
            <p className="eyebrow">{t(locale, "app.eyebrow")}</p>
            <div className="brandTitleRow">
              <h1>{productTitle}</h1>
              <span className="versionBadge">v{APP_VERSION}</span>
            </div>
          </div>
        </div>
        <div className="toolbarActions">
          {session.authMode !== "none" ? (
            session.authenticated ? (
              <span className="authBadge">{t(locale, "auth.loggedIn")}</span>
            ) : (
              <>
                <input
                  className="passwordInput"
                  type="password"
                  value={password}
                  placeholder={t(locale, "auth.serverPassword")}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <button type="button" onClick={() => void login()}>
                  <KeyRound size={18} />
                  {t(locale, "auth.login")}
                </button>
              </>
            )
          ) : null}
          <div ref={toolbarMenusRef} className="toolbarMenus" aria-label="Display controls">
            <details ref={languageMenuRef} className="iconMenu">
              <summary
                aria-label={t(locale, "settings.language")}
                title={t(locale, "settings.language")}
                onClick={() => closeDetailsMenus(themeMenuRef.current)}
              >
                <Languages size={18} />
                <span>{locale === "ko" ? "KO" : "EN"}</span>
              </summary>
              <div className="iconMenuPanel">
                <button
                  type="button"
                  className={locale === "ko" ? "activeMenuItem" : ""}
                  onClick={() => {
                    setLocale("ko");
                    closeToolbarMenus();
                  }}
                >
                  KO
                  <span>{LOCALE_LABELS.ko}</span>
                </button>
                <button
                  type="button"
                  className={locale === "en" ? "activeMenuItem" : ""}
                  onClick={() => {
                    setLocale("en");
                    closeToolbarMenus();
                  }}
                >
                  EN
                  <span>{LOCALE_LABELS.en}</span>
                </button>
              </div>
            </details>
            <details ref={themeMenuRef} className="iconMenu">
              <summary
                aria-label={t(locale, "settings.theme")}
                title={t(locale, "settings.theme")}
                onClick={() => closeDetailsMenus(languageMenuRef.current)}
              >
                <ThemeIcon size={18} />
                <span>{appearanceLabel(appearance, locale)}</span>
              </summary>
              <div className="iconMenuPanel">
                {(["system", "light", "dark"] as Appearance[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={appearance === item ? "activeMenuItem" : ""}
                    onClick={() => {
                      setAppearance(item);
                      closeToolbarMenus();
                    }}
                  >
                    {item === "system" ? <Monitor size={16} /> : item === "light" ? <Sun size={16} /> : <Moon size={16} />}
                    <span>{appearanceLabel(item, locale)}</span>
                  </button>
                ))}
              </div>
            </details>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.currentTarget.value = "";
            }}
            hidden
          />
          <button type="button" className="primaryButton" onClick={() => fileInputRef.current?.click()}>
            <ImageUp size={18} />
            {t(locale, "actions.uploadImage")}
          </button>
          {batchItems.length > 1 ? (
            <button
              type="button"
              onClick={() => void runBatchQueue()}
              disabled={!batchItems.length || isBusy || isBatchRunning}
            >
              {isBatchRunning ? <Loader2 className="spin" size={18} /> : <ScanSearch size={18} />}
              {t(locale, "batch.runAll")}
            </button>
          ) : null}
          <button type="button" onClick={runDetection} disabled={!image || isBusy}>
            {isBusy ? <Loader2 className="spin" size={18} /> : <ScanSearch size={18} />}
            {t(locale, "actions.runDetection")}
          </button>
          <button type="button" onClick={() => void renderMaskedImage("png")} disabled={!image || maskedCount === 0}>
            <Download size={18} />
            {t(locale, "actions.previewResult")}
          </button>
        </div>
      </section>

      <section className="workspace">
        <aside className="sidePanel">
          <Panel
            title={t(locale, "panels.preset")}
            open={panelState.preset}
            onOpenChange={(open) => setPanelOpen("preset", open)}
          >
            <div className="checkGrid">
              {ALL_CATEGORIES.map((category) => (
                <label key={category} className="checkItem">
                  <input
                    type="checkbox"
                    checked={categories.includes(category)}
                    onChange={(event) => {
                      setCategories((current) =>
                        event.target.checked
                          ? [...current, category]
                          : current.filter((item) => item !== category)
                      );
                    }}
                  />
                  {categoryLabel(category, locale)}
                </label>
              ))}
            </div>
          </Panel>

          <Panel
            title={t(locale, "panels.detectionOptions")}
            open={panelState.detectionOptions}
            onOpenChange={(open) => setPanelOpen("detectionOptions", open)}
          >
            <label className="switchRow">
              <input
                type="checkbox"
                checked={deepSearch}
                onChange={(event) => setDeepSearch(event.target.checked)}
              />
              {t(locale, "detection.deepSearch")}
            </label>
            <p className="muted">{t(locale, "detection.deepSearchHint")}</p>
          </Panel>

          <Panel
            title={t(locale, "panels.batch")}
            open={panelState.batch}
            onOpenChange={(open) => setPanelOpen("batch", open)}
          >
            {batchItems.length ? (
              <>
                <div className="batchList">
                  {batchItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={item.id === activeBatchItemId ? "batchItem activeBatchItem" : "batchItem"}
                      onClick={() => loadBatchItem(item)}
                    >
                      {item.image ? <img src={item.image.dataUrl} alt="" /> : null}
                      <span>
                        <strong>{item.fileName}</strong>
                        <small>
                          {batchStatusLabel(item.status, locale)}
                          {item.status === "done" ? ` · ${item.boxes.length}${locale === "ko" ? "개" : ""}` : ""}
                          {item.error ? ` · ${item.error}` : ""}
                        </small>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="batchActions">
                  <button
                    type="button"
                    onClick={() => void runBatchQueue()}
                    disabled={!batchItems.length || isBusy || isBatchRunning}
                  >
                    {isBatchRunning ? <Loader2 className="spin" size={16} /> : <ScanSearch size={16} />}
                    {t(locale, "batch.runAll")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void downloadBatchZip()}
                    disabled={!batchZipReady || isBusy}
                  >
                    <Download size={16} />
                    {t(locale, "batch.downloadZip")}
                  </button>
                </div>
                <p className="muted">{t(locale, "batch.summary", { done: completedBatchCount, total: batchItems.length })}</p>
              </>
            ) : (
              <p className="muted">{t(locale, "batch.empty")}</p>
            )}
          </Panel>

          <Panel
            title={t(locale, "panels.maskOptions")}
            open={panelState.maskOptions}
            onOpenChange={(open) => setPanelOpen("maskOptions", open)}
          >
            <label className="field">
              <span>{t(locale, "mask.mode")}</span>
              <select
                value={maskSettings.mode}
                onChange={(event) =>
                  setMaskSettings((current) => ({ ...current, mode: event.target.value as MaskSettings["mode"] }))
                }
              >
                <option value="opaque">{t(locale, "mask.opaque")}</option>
                <option value="pixelate">{t(locale, "mask.pixelate")}</option>
                <option value="blur">{t(locale, "mask.blur")}</option>
              </select>
            </label>
            <label className="field">
              <span>{t(locale, "mask.pixelStrength")} {maskSettings.pixelSize}px</span>
              <input
                type="range"
                min="4"
                max="32"
                value={maskSettings.pixelSize}
                onChange={(event) =>
                  setMaskSettings((current) => ({ ...current, pixelSize: Number(event.target.value) }))
                }
              />
            </label>
            <label className="field">
              <span>{t(locale, "mask.blurStrength")} {maskSettings.blurRadius}px</span>
              <input
                type="range"
                min="2"
                max="28"
                value={maskSettings.blurRadius}
                onChange={(event) =>
                  setMaskSettings((current) => ({ ...current, blurRadius: Number(event.target.value) }))
                }
              />
            </label>
          </Panel>

          {LOCAL_AI_FEATURE_ENABLED ? (
            <Panel
              title={t(locale, "panels.localAi")}
              open={panelState.localAi}
              onOpenChange={(open) => setPanelOpen("localAi", open)}
            >
              <label className="switchRow">
                <input
                  type="checkbox"
                  checked={localAiSettings.enabled}
                  onChange={(event) => {
                    setLocalAiSettings((current) => ({ ...current, enabled: event.target.checked }));
                    setLocalAiStatus("idle");
                  }}
                />
                {t(locale, "localAi.enable")}
              </label>
              <p className="muted">{t(locale, "localAi.hint")}</p>
              <label className="field">
                <span>{t(locale, "localAi.endpoint")}</span>
                <input
                  type="text"
                  value={localAiSettings.endpoint}
                  onChange={(event) => {
                    setLocalAiSettings((current) => ({ ...current, endpoint: event.target.value }));
                    setLocalAiStatus("idle");
                  }}
                />
              </label>
              <label className="field">
                <span>{t(locale, "localAi.profile")}</span>
                <select
                  value={localAiSettings.profile}
                  onChange={(event) =>
                    setLocalAiSettings((current) => ({
                      ...current,
                      profile: event.target.value as LocalAiProfile
                    }))
                  }
                >
                  <option value="fast">{t(locale, "localAi.fast")}</option>
                  <option value="precise">{t(locale, "localAi.precise")}</option>
                </select>
              </label>
              <p className={`muted localAiStatus localAiStatus-${localAiStatus}`}>
                {localAiStatus === "ready"
                  ? t(locale, "localAi.statusReady")
                  : localAiStatus === "unavailable"
                    ? t(locale, "localAi.statusUnavailable")
                    : t(locale, "localAi.statusIdle")}
              </p>
              {localAiRuntimeEnabled && detectionDiagnostics ? (
                <p className="muted">{localAiRuntimeDiagnosticLabel(detectionDiagnostics, locale)}</p>
              ) : null}
            </Panel>
          ) : null}
        </aside>

        <section className="canvasPanel">
          <div className="statusBar">
            <span>{statusText}</span>
            <span>
              {t(locale, "counts.maskedIgnored", { masked: maskedCount, ignored: ignoredCount })}
            </span>
          </div>
          {detectionDiagnostics ? (
            <div className="diagnosticsBar">
              <span>
                {t(locale, "diagnostics.summary", {
                  lines: detectionDiagnostics.ocr.lineCount,
                  words: detectionDiagnostics.ocr.wordCount,
                  urlLines: detectionDiagnostics.urlOcr.lineCount,
                  urlWords: detectionDiagnostics.urlOcr.wordCount,
                  enhancedLines: detectionDiagnostics.urlOcr.enhancedLineCount ?? 0,
                  enhancedWords: detectionDiagnostics.urlOcr.enhancedWordCount ?? 0,
                  cropLines: detectionDiagnostics.urlOcr.cropLineCount ?? 0,
                  cropWords: detectionDiagnostics.urlOcr.cropWordCount ?? 0,
                  enhancedMode: diagnosticsEnhancedMode(detectionDiagnostics.urlOcr, locale),
                  cache: diagnosticsCacheLabel(detectionDiagnostics, locale),
                  ocrMs: diagnosticsElapsedMs(detectionDiagnostics),
                  chars: detectionDiagnostics.ocr.rawTextLength,
                  patterns: detectionDiagnostics.patternCount,
                  recovered: detectionDiagnostics.recoveredCandidateCount,
                  layout: detectionDiagnostics.layoutCandidateCount,
                  contexts: detectionDiagnostics.contextCandidateCount,
                  faces: detectionDiagnostics.faceCount,
                  faceEngine: diagnosticsFaceEngineLabel(detectionDiagnostics.faceDiagnostics, locale),
                  faceMs: detectionDiagnostics.faceDiagnostics.elapsedMs ?? 0,
                  localAi: detectionDiagnostics.localAiCount,
                  aiReviewed: detectionDiagnostics.localAiReviewedCount,
                  aiDropped: detectionDiagnostics.localAiDroppedCount,
                  aiChanged: detectionDiagnostics.localAiChangedCount,
                  aiNeedsReview: detectionDiagnostics.localAiNeedsReviewCount,
                  vlm: detectionDiagnostics.vlmCount,
                  vlmTiles: detectionDiagnostics.vlmTileCount,
                  vlmRejected: detectionDiagnostics.vlmRejectedCount,
                  vlmInvalid: detectionDiagnostics.vlmInvalidCategoryCount,
                  vlmPlaceholder: detectionDiagnostics.vlmPlaceholderRejectedCount,
                  vlmParseErrors: detectionDiagnostics.vlmParseErrorCount,
                  vlmModel: detectionDiagnostics.vlmModel ?? "-",
                  vlmHttp: detectionDiagnostics.vlmHttpStatus ?? "-",
                  vlmSchema: diagnosticsVlmSchemaLabel(detectionDiagnostics, locale),
                  aiFallback: diagnosticsAiFallbackLabel(detectionDiagnostics, locale),
                  aiMs: detectionDiagnostics.aiElapsedMs
                })}
              </span>
              {detectionDiagnostics.ocr.usedFallback ? (
                <span>{t(locale, "diagnostics.fallback")}</span>
              ) : null}
            </div>
          ) : null}
          <div className="imageStage">
            {isDraggingFile ? (
              <div className="dropOverlay">
                <strong>{t(locale, "drop.title")}</strong>
                <span>{t(locale, "drop.hint")}</span>
              </div>
            ) : null}
            {image ? (
              <div className="imageFrame">
                <img
                  ref={imageRef}
                  src={image.url}
                  alt={t(locale, "empty.chooseImage")}
                  draggable={false}
                  onLoad={(event) => {
                    const target = event.currentTarget;
                    setImage((current) =>
                      current ? { ...current, width: target.naturalWidth, height: target.naturalHeight } : current
                    );
                  }}
                />
                <svg
                  ref={overlayRef}
                  className="overlay"
                  viewBox={`0 0 ${image.width} ${image.height}`}
                  onPointerDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    event.currentTarget.setPointerCapture(event.pointerId);
                    setSelectedBoxId(null);
                    const point = pointerToImagePoint(event);
                    setDrag({ start: point, current: point });
                  }}
                  onPointerMove={(event) => {
                    if (boxEditDrag) {
                      updateBoxEdit(event);
                      return;
                    }
                    if (!drag) return;
                    setDrag({ ...drag, current: pointerToImagePoint(event) });
                  }}
                  onPointerUp={() => {
                    if (boxEditDrag) {
                      finishBoxEdit();
                      return;
                    }
                    finishManualBox();
                  }}
                  onPointerCancel={() => {
                    setDrag(null);
                    setBoxEditDrag(null);
                  }}
                >
                  {displayBoxes.map((box) => (
                    <rect
                      key={box.id}
                      className={`${box.action === "mask" ? "maskRect" : "ignoreRect"}${
                        box.id === selectedBoxId ? " selectedRect" : ""
                      }`}
                      x={box.bbox.x}
                      y={box.bbox.y}
                      width={box.bbox.width}
                      height={box.bbox.height}
                      onPointerDown={(event) => {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        startBoxEdit(event, box, "move");
                      }}
                    />
                  ))}
                  {selectedBox ? (
                    <SelectionControls
                      box={selectedBox}
                      imageWidth={image.width}
                      deleteLabel={t(locale, "box.deleteShort")}
                      deleteAriaLabel={t(locale, "box.delete")}
                      onResizeStart={(event, handle) => {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        startBoxEdit(event, selectedBox, handle);
                      }}
                      onDelete={() => removeBox(selectedBox.id)}
                    />
                  ) : null}
                  {drag ? <ManualPreview rect={normalizeRect(drag.start, drag.current)} /> : null}
                </svg>
              </div>
            ) : (
              <button type="button" className="emptyState" onClick={() => fileInputRef.current?.click()}>
                <SquareDashedMousePointer size={32} />
                {t(locale, "empty.chooseImage")}
              </button>
            )}
          </div>
        </section>

        <aside className="sidePanel">
          <Panel
            title={t(locale, "panels.detections")}
            open={panelState.detections}
            onOpenChange={(open) => setPanelOpen("detections", open)}
          >
            <div className="historyButtons">
              <button type="button" onClick={() => dispatchBoxes({ type: "undo" })} disabled={!boxesState.past.length}>
                <Undo2 size={16} />
                {t(locale, "actions.undo")}
              </button>
              <button type="button" onClick={() => dispatchBoxes({ type: "redo" })} disabled={!boxesState.future.length}>
                <Redo2 size={16} />
                {t(locale, "actions.redo")}
              </button>
            </div>
            <div className="boxList" ref={boxListRef}>
              {activeBoxes.length === 0 ? (
                <p className="muted">{t(locale, "empty.noDetections")}</p>
              ) : (
                activeBoxes.map((box) => (
                  <article
                    key={box.id}
                    data-box-id={box.id}
                    role="button"
                    tabIndex={0}
                    className={`${box.action === "mask" ? "boxItem" : "boxItem ignored"}${
                      box.id === selectedBoxId ? " selectedBoxItem" : ""
                    }`}
                    onClick={() => setSelectedBoxId(box.id)}
                    onKeyDown={(event) => handleBoxItemKeyDown(event, box.id)}
                  >
                    <div>
                      <strong>{categoryLabel(box.category, locale)}</strong>
                      <span>{sourceLabel(box.source, locale)} · {Math.round(box.confidence * 100)}%</span>
                      <span className="candidateBadge">{candidateStatusLabel(box, locale)}</span>
                      {box.requiresReview ? <span className="reviewBadge">{t(locale, "localAi.requiresReview")}</span> : null}
                      {box.text && TEXT_CATEGORIES.has(box.category) ? (
                        <code>
                          {box.text}
                        </code>
                      ) : null}
                      {box.reason ? (
                        <span className="boxReason">{t(locale, "localAi.reason")}: {box.reason}</span>
                      ) : null}
                      {box.evidenceText ? <span className="boxEvidence">{box.evidenceText}</span> : null}
                    </div>
                    <div className="rowActions">
                      <button
                        type="button"
                        title={t(locale, "box.toggle")}
                        onClick={(event) => {
                          event.stopPropagation();
                          void toggleBox(box);
                        }}
                      >
                        {box.action === "mask" ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        type="button"
                        title={t(locale, "box.delete")}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeBox(box.id);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </Panel>
          <Panel
            title={t(locale, "panels.export")}
            open={panelState.export}
            onOpenChange={(open) => setPanelOpen("export", open)}
          >
            <div className="exportButtons">
              <button type="button" onClick={() => void renderMaskedImage("png")} disabled={!image || maskedCount === 0}>
                <Eye size={18} />
                {t(locale, "actions.previewResult")}
              </button>
              <button type="button" onClick={() => void renderMaskedImage("png")} disabled={!image || maskedCount === 0}>
                <Eye size={18} />
                {t(locale, "actions.previewPng")}
              </button>
              <button type="button" onClick={() => void renderMaskedImage("jpeg")} disabled={!image || maskedCount === 0}>
                <Eye size={18} />
                {t(locale, "actions.previewJpg")}
              </button>
            </div>
            <p className="muted">{t(locale, "export.exif")}</p>
          </Panel>
        </aside>
      </section>
      <footer className="appFooter">
        <span>{CREATOR_LABEL}</span>
        <span className="footerDivider" aria-hidden="true">·</span>
        <a href={GUIDE_URL} target="_blank" rel="noreferrer">
          가이드
        </a>
      </footer>
      {isResultOpen && renderedImage ? (
        <section
          className="resultModal"
          role="dialog"
          aria-modal="true"
          aria-label={t(locale, "panels.result")}
          onPointerDown={(event) => {
            if (isDirectBackdropPointer(event.target, event.currentTarget)) {
              setIsResultOpen(false);
            }
          }}
        >
          <div className="resultDialog">
            <header>
              <h2>{t(locale, "panels.result")}</h2>
              <button type="button" onClick={() => setIsResultOpen(false)}>
                {t(locale, "actions.close")}
              </button>
            </header>
            <div className="resultPreview">
              <img src={renderedImage.url} alt={t(locale, "panels.result")} />
            </div>
            <footer>
              <button type="button" onClick={() => void renderMaskedImage(renderedImage.outputType, { notify: true })}>
                <ScanSearch size={18} />
                {t(locale, "actions.renderAgain")}
              </button>
              <button type="button" onClick={() => void copyRenderedImage()}>
                <Copy size={18} />
                {t(locale, "actions.copyImage")}
              </button>
              <button type="button" onClick={() => void downloadRenderedImage("png")}>
                <Download size={18} />
                {t(locale, "actions.downloadPng")}
              </button>
              <button type="button" onClick={() => void downloadRenderedImage("jpeg")}>
                <Download size={18} />
                {t(locale, "actions.downloadJpg")}
              </button>
            </footer>
          </div>
        </section>
      ) : null}
      {toast ? (
        <div className="toast" role="status" aria-live="polite">
          {t(locale, toast.key, toast.values)}
        </div>
      ) : null}
    </main>
  );
}

function Panel({
  title,
  open,
  onOpenChange,
  children
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <details className="panel collapsiblePanel" open={open} onToggle={(event) => onOpenChange(event.currentTarget.open)}>
      <summary>
        <h2>{title}</h2>
        <ChevronDown className="collapsibleIcon" size={18} />
      </summary>
      <div className="panelBody">{children}</div>
    </details>
  );
}

function SelectionControls({
  box,
  imageWidth,
  deleteLabel,
  deleteAriaLabel,
  onResizeStart,
  onDelete
}: {
  box: DetectionBox;
  imageWidth: number;
  deleteLabel: string;
  deleteAriaLabel: string;
  onResizeStart: (event: PointerEvent<SVGRectElement>, handle: ResizeHandle) => void;
  onDelete: () => void;
}) {
  const handleSize = 10;
  const toolbar = selectionToolbarRect(box.bbox, imageWidth);

  return (
    <g className="selectionControls">
      {resizeHandlePoints(box.bbox).map(({ handle, x, y }) => (
        <rect
          key={handle}
          className={`resizeHandle resizeHandle-${handle}`}
          x={x - handleSize / 2}
          y={y - handleSize / 2}
          width={handleSize}
          height={handleSize}
          rx={2}
          onPointerDown={(event) => onResizeStart(event, handle)}
        />
      ))}
      <foreignObject x={toolbar.x} y={toolbar.y} width={toolbar.width} height={toolbar.height}>
        <button
          type="button"
          className="canvasDeleteButton"
          aria-label={deleteAriaLabel}
          title={deleteAriaLabel}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
          {deleteLabel}
        </button>
      </foreignObject>
    </g>
  );
}

function resizeHandlePoints(rect: Rect): Array<{ handle: ResizeHandle; x: number; y: number }> {
  const left = rect.x;
  const centerX = rect.x + rect.width / 2;
  const right = rect.x + rect.width;
  const top = rect.y;
  const centerY = rect.y + rect.height / 2;
  const bottom = rect.y + rect.height;
  return [
    { handle: "nw", x: left, y: top },
    { handle: "n", x: centerX, y: top },
    { handle: "ne", x: right, y: top },
    { handle: "e", x: right, y: centerY },
    { handle: "se", x: right, y: bottom },
    { handle: "s", x: centerX, y: bottom },
    { handle: "sw", x: left, y: bottom },
    { handle: "w", x: left, y: centerY }
  ];
}

function selectionToolbarRect(rect: Rect, imageWidth: number): Rect {
  const width = 64;
  const height = 30;
  const x = Math.min(Math.max(0, rect.x), Math.max(0, imageWidth - width));
  const y = rect.y > height + 8 ? rect.y - height - 8 : rect.y + 8;
  return { x, y, width, height };
}

function ManualPreview({ rect }: { rect: Rect }) {
  return (
    <rect
      className="manualPreview"
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
    />
  );
}

function normalizeRect(start: { x: number; y: number }, current: { x: number; y: number }): Rect {
  const x = Math.min(start.x, current.x);
  const y = Math.min(start.y, current.y);
  return {
    x,
    y,
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y)
  };
}

function rectsEqual(left: Rect, right: Rect): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}

function appearanceLabel(appearance: Appearance, locale: Locale): string {
  if (appearance === "system") return t(locale, "settings.system");
  if (appearance === "light") return t(locale, "settings.light");
  return t(locale, "settings.dark");
}

function batchStatusLabel(status: BatchItem["status"], locale: Locale): string {
  if (status === "running") return t(locale, "batch.running");
  if (status === "done") return t(locale, "batch.done");
  if (status === "failed") return t(locale, "batch.failed");
  return t(locale, "batch.pending");
}

function candidateStatusLabel(box: DetectionBox, locale: Locale): string {
  if (box.source === "local-llm" || box.source === "local-vlm") return t(locale, "localAi.reviewedCandidate");
  if (box.requiresReview || classifyCandidateStrength(box) === "weak") return t(locale, "localAi.needsReviewCandidate");
  return t(locale, "localAi.strongCandidate");
}

function emptyOcrRecognition(enhancedSkipped = false): OcrRecognition {
  return {
    lines: [],
    rawText: "",
    diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, enhancedSkipped, elapsedMs: 0, usedFallback: false }
  };
}

function emptyFaceDiagnostics(): FaceDetectionDiagnostics {
  return { engine: "unavailable", faceCount: 0, elapsedMs: 0 };
}

function diagnosticsEnhancedMode(urlOcr: OcrDiagnostics, locale: Locale): string {
  if (urlOcr.enhancedSkipped === undefined) return locale === "ko" ? "해당 없음" : "n/a";
  if (urlOcr.enhancedSkipped) return locale === "ko" ? "건너뜀" : "skipped";
  return locale === "ko" ? "실행" : "ran";
}

function diagnosticsCacheLabel(diagnostics: DetectionDiagnostics, locale: Locale): string {
  const cacheHit = diagnostics.ocr.cacheHit || diagnostics.urlOcr.cacheHit;
  if (cacheHit) return locale === "ko" ? "캐시 사용" : "cache hit";
  return locale === "ko" ? "새 OCR" : "fresh OCR";
}

function diagnosticsElapsedMs(diagnostics: DetectionDiagnostics): number {
  return Math.round((diagnostics.ocr.elapsedMs ?? 0) + (diagnostics.urlOcr.elapsedMs ?? 0));
}

function diagnosticsFaceEngineLabel(diagnostics: FaceDetectionDiagnostics | undefined, locale: Locale): string {
  const engine = diagnostics?.engine ?? "unavailable";
  if (engine === "native") return locale === "ko" ? "native" : "native";
  if (engine === "mediapipe") return locale === "ko" ? "mediapipe" : "mediapipe";
  if (engine === "human") return locale === "ko" ? "human" : "human";
  const errorCode = diagnostics?.errorCode ? `/${diagnostics.errorCode}` : "";
  const errorMessage = diagnostics?.errorMessage ? `:${diagnostics.errorMessage.slice(0, 80)}` : "";
  return locale === "ko" ? `없음${errorCode}${errorMessage}` : `unavailable${errorCode}${errorMessage}`;
}

function diagnosticsVlmSchemaLabel(diagnostics: DetectionDiagnostics, locale: Locale): string {
  if (diagnostics.vlmSchemaMode === undefined) return locale === "ko" ? "해당 없음" : "n/a";
  return diagnostics.vlmSchemaMode ? "schema" : "json";
}

function parseHttpStatusFromError(message: string): number | undefined {
  const match = message.match(/status\s+(\d{3})/i);
  if (!match) return undefined;
  return Number.parseInt(match[1], 10);
}

function localAiRuntimeDiagnosticLabel(diagnostics: DetectionDiagnostics, locale: Locale): string {
  if (diagnostics.vlmFallback) {
    return locale === "ko" ? "로컬 AI 연결 실패로 빠른 엔진으로 대체했습니다." : "Local AI failed; fast engine fallback was used.";
  }
  if (diagnostics.vlmHttpStatus && diagnostics.vlmHttpStatus >= 400) {
    return locale === "ko"
      ? `모델 응답 오류: HTTP ${diagnostics.vlmHttpStatus}`
      : `Model response error: HTTP ${diagnostics.vlmHttpStatus}`;
  }
  if (diagnostics.vlmParseErrorCount > 0) {
    return locale === "ko"
      ? "연결은 됐지만 모델 응답을 JSON 박스로 해석하지 못했습니다."
      : "Connected, but the model response could not be parsed as JSON boxes.";
  }
  if (diagnostics.vlmPlaceholderRejectedCount > 0 || diagnostics.vlmInvalidCategoryCount > 0) {
    return locale === "ko"
      ? "모델 응답에서 placeholder 또는 잘못된 카테고리를 폐기했습니다."
      : "Placeholder or invalid-category model responses were rejected.";
  }
  if (
    diagnostics.faceDiagnostics.engine === "mediapipe" ||
    diagnostics.faceDiagnostics.engine === "native" ||
    diagnostics.faceDiagnostics.engine === "human"
  ) {
    return locale === "ko"
      ? `전용 얼굴 감지: ${diagnostics.faceDiagnostics.engine}, ${diagnostics.faceCount}개`
      : `Dedicated face detection: ${diagnostics.faceDiagnostics.engine}, ${diagnostics.faceCount}`;
  }
  return locale === "ko" ? "로컬 VLM 연결 가능" : "Local VLM connected";
}

function diagnosticsAiFallbackLabel(diagnostics: DetectionDiagnostics, locale: Locale): string {
  if (diagnostics.vlmFallback) return locale === "ko" ? "fallback" : "fallback";
  if (diagnostics.vlmCount > 0 || diagnostics.vlmTileCount > 0) return locale === "ko" ? "직접" : "direct";
  return locale === "ko" ? "꺼짐" : "off";
}

function buildVlmTiles(
  targetImage: LoadedImage,
  targetElement: HTMLImageElement,
  profile: LocalAiProfile,
  maxEdge: number
): LocalAiVlmTile[] {
  return planVlmTiles({
    width: targetImage.width,
    height: targetImage.height,
    profile
  }).map((tile) => ({
    ...tile,
    dataUrl: createLocalAiCropDataUrl(targetElement, tile.bbox, maxEdge)
  }));
}

function createLocalAiPreviewDataUrl(image: HTMLImageElement, maxEdge = 1280): string {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return image.src;
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.86);
}

function createLocalAiCropDataUrl(image: HTMLImageElement, bbox: Rect, maxEdge = 512): string {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const sx = Math.max(0, Math.min(sourceWidth, Math.round(bbox.x)));
  const sy = Math.max(0, Math.min(sourceHeight, Math.round(bbox.y)));
  const sw = Math.max(1, Math.min(sourceWidth - sx, Math.round(bbox.width)));
  const sh = Math.max(1, Math.min(sourceHeight - sy, Math.round(bbox.height)));
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const width = Math.max(1, Math.round(sw * scale));
  const height = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return image.src;
  context.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.86);
}

function readImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error("이미지를 읽지 못했습니다."));
    image.src = dataUrl;
  });
}

function loadHtmlImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("이미지를 읽지 못했습니다."));
    image.src = dataUrl;
  });
}
